// Typed wrappers over the May 2026 evaluation API.
//
// Contract: specs/May2026_01_Evaluation_BE.md §API contract.
// Endpoints route to VITE_API_URL_EVALUATION via apiClient's prefix map.

import { apiClient } from '@/lib/apiClient'
import type {
  AnswerResponse,
  EvalStartResponse,
  EvaluationResultsV2,
  ProbeAnswerPayload,
} from '@/types'

// `answer` shape varies by question type:
//   - Q1/Q2 (single-select): string  (the option label)
//   - Q3    (multi-select):  string[] (option labels)
//   - probes (A–F):          ProbeAnswerPayload
export type SubmitAnswerPayload =
  | { questionId: string; answer: string }
  | { questionId: string; answer: string[] }
  | { questionId: string; answer: ProbeAnswerPayload }

export async function startEvaluation(
  learnerId: string,
): Promise<EvalStartResponse> {
  const { data } = await apiClient.post<EvalStartResponse>(
    '/api/evaluations',
    { learnerId },
  )
  return data
}

export async function submitAnswer(
  evaluationId: string,
  payload: SubmitAnswerPayload,
): Promise<AnswerResponse> {
  const { data } = await apiClient.post<AnswerResponse>(
    `/api/evaluations/${evaluationId}/answer`,
    payload,
  )
  // `completedAt` arrives as ISO string on the `complete` phase; deserialize
  // at the boundary so consumers can rely on Date.
  if (data.phase === 'complete' && typeof data.result.completedAt === 'string') {
    data.result = {
      ...data.result,
      completedAt: new Date(data.result.completedAt as unknown as string),
    }
  }
  return data
}

export async function fetchLatestEvaluation(
  learnerId: string,
): Promise<EvaluationResultsV2> {
  const { data } = await apiClient.get<EvaluationResultsV2>(
    `/api/evaluations/${learnerId}/latest`,
  )
  if (typeof data.completedAt === 'string') {
    data.completedAt = new Date(data.completedAt as unknown as string)
  }
  return data
}

// Phase B (2026-05-30) — sets the mode-question answer on a completed
// evaluation. Required because the framework asks the mode question AFTER
// probes complete (retrospective); the BE accepts this via a dedicated PATCH
// instead of bundling on the first probe payload.
//
// See specs/May2026_01_Evaluation_BE_ticket_mode_question_relocation.md
// and the BE companion doc's "PATCH endpoint" section.
//
// Behavior:
//   - 400 EVALUATION_NOT_FOUND if eval id missing or owned by different user
//   - 409 EVALUATION_NOT_COMPLETED if eval status != COMPLETED
//   - Idempotent; last-write-wins on different values
//   - Returns reconstructed EvaluationResults so callers can refresh local
//     cache from the response
export async function setChildPresent(
  evaluationId: string,
  childPresent: boolean,
): Promise<EvaluationResultsV2> {
  const { data } = await apiClient.patch<EvaluationResultsV2>(
    `/api/evaluations/${evaluationId}`,
    { childPresent },
  )
  // BE returns completedAt as an ISO string per existing convention.
  if (typeof data.completedAt === 'string') {
    data.completedAt = new Date(data.completedAt as unknown as string)
  }
  return data
}
